/* ==========================================================================
   ORKA — Buy Box
   --------------------------------------------------------------------------
   Drives the product buy box:

   - Flavours are separate PRODUCTS, not variants. Every flavour's data and
     gallery is rendered up front, so switching is instant with no reload;
     the URL is swapped with replaceState so the page stays shareable.
   - A pack size buys the flavour's matching variant (flavor.packVariants,
     resolved server-side) once. A flavour with no variant for that pack falls
     back to a quantity multiplier on the base product (12-pack ×2 = 24).
     Any money-off is configured as an automatic discount in Shopify admin;
     `discount` here only mirrors it in the displayed price.
   - Subscribe / one-time reads real selling plans off the product.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents, format) {
    var value = Math.round(cents);
    var fmt = format || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var tail = parts[1] ? decimal + parts[1] : '';
      return head + tail;
    }

    var placeholder = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var name = placeholder ? placeholder[1] : 'amount';
    var out;

    switch (name) {
      case 'amount_no_decimals':
        out = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(value, 2, "'", '.');
        break;
      default:
        out = withDelimiters(value, 2, ',', '.');
    }

    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  /* ------------------------------------------------------- delivery dates */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function ordinal(n) {
    var s = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  function formatDate(date) {
    return MONTHS[date.getMonth()] + ' ' + ordinal(date.getDate());
  }

  function addDays(date, count, skipWeekends) {
    var out = new Date(date.getTime());
    var added = 0;
    while (added < count) {
      out.setDate(out.getDate() + 1);
      var day = out.getDay();
      if (skipWeekends && (day === 0 || day === 6)) continue;
      added++;
    }
    return out;
  }

  function deliveryRange(cfg) {
    var now = new Date();
    var start = new Date(now.getTime());

    // Past the cut-off, the order ships the next day.
    if (now.getHours() >= cfg.cutoffHour) {
      start.setDate(start.getDate() + 1);
    }

    var min = addDays(start, cfg.minDays, cfg.skipWeekends);
    var max = addDays(start, cfg.maxDays, cfg.skipWeekends);

    return cfg.maxDays > cfg.minDays ? formatDate(min) + '-' + formatDate(max) : formatDate(min);
  }

  /* ------------------------------------------------------------- buy box */

  function BuyBox(root) {
    if (root.dataset.orkaBuyBoxReady === 'true') return;
    root.dataset.orkaBuyBoxReady = 'true';

    this.root = root;

    var payload = root.querySelector('[data-obb-data]');
    if (!payload) return;

    try {
      this.data = JSON.parse(payload.textContent);
    } catch (e) {
      console.warn('[Orka] Buy box data could not be parsed.', e);
      return;
    }

    if (!this.data.flavors || !this.data.flavors.length) return;

    this.flavorIndex = this.data.activeFlavor || 0;
    this.packIndex = this.data.activePack || 0;
    this.purchase = this.data.subscribeDefault ? 'subscribe' : 'onetime';
    this.planId = null;
    this.galleries = {};
    // The server already rendered Yotpo for the active flavour; seed this so
    // the first update() doesn't race Yotpo's own async mount by reinit'ing
    // a widget that hasn't finished loading yet.
    this.yotpoProductId = this.data.flavors[this.flavorIndex] && this.data.flavors[this.flavorIndex].id;

    this.cache();
    this.bind();

    var self = this;
    document.addEventListener('ajaxProduct:added', function () {
      self.verifyAgainstCart();
    });
    this.initGalleries();
    this.renderEta();
    this.update();
  }

  BuyBox.prototype.cache = function () {
    var r = this.root;
    this.el = {
      flavors: r.querySelectorAll('[data-obb-flavor]'),
      packs: r.querySelectorAll('[data-obb-pack]'),
      options: r.querySelectorAll('[data-obb-option]'),
      planSelect: r.querySelector('[data-obb-plan-select]'),
      title: r.querySelector('[data-obb-title]'),
      priceCurrent: r.querySelector('[data-obb-price-current]'),
      priceCompare: r.querySelector('[data-obb-price-compare]'),
      subPrice: r.querySelector('[data-obb-sub-price]'),
      subUnit: r.querySelector('[data-obb-sub-unit]'),
      subBadge: r.querySelector('[data-obb-sub-badge]'),
      otPrice: r.querySelector('[data-obb-ot-price]'),
      otUnit: r.querySelector('[data-obb-ot-unit]'),
      variantInput: r.querySelector('[data-obb-variant-id]'),
      qtyInput: r.querySelector('[data-obb-quantity]'),
      planInput: r.querySelector('[data-obb-selling-plan]'),
      atc: r.querySelector('[data-obb-atc]'),
      atcText: r.querySelector('[data-obb-atc-text]'),
      eta: r.querySelector('[data-obb-eta]'),
      galleries: r.querySelectorAll('[data-obb-gallery]')
    };
  };

  BuyBox.prototype.bind = function () {
    var self = this;

    this.el.flavors.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.flavorIndex = parseInt(btn.dataset.obbFlavor, 10) || 0;
        self.planId = null; // plans differ per product
        self.update();
        self.syncUrl();
      });
    });

    this.el.packs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.packIndex = parseInt(btn.dataset.obbPack, 10) || 0;
        self.update();
        self.syncUrl();
      });
    });

    this.el.options.forEach(function (opt) {
      function choose(evt) {
        // The frequency dropdown lives inside the subscribe card; interacting
        // with it must not be swallowed by the card's own click handling.
        if (evt.target.closest('[data-obb-plan-select]')) return;
        self.purchase = opt.dataset.obbOption;
        self.update();
      }

      opt.addEventListener('click', choose);
      opt.addEventListener('keydown', function (evt) {
        if (evt.key === ' ' || evt.key === 'Enter') {
          evt.preventDefault();
          choose(evt);
        }
      });
    });

    if (this.el.planSelect) {
      this.el.planSelect.addEventListener('change', function () {
        self.planId = parseInt(this.value, 10) || null;
        self.purchase = 'subscribe';
        self.update();
      });
    }
  };

  /* Galleries are pre-rendered per flavour; we only toggle visibility. */
  BuyBox.prototype.initGalleries = function () {
    var self = this;

    this.el.galleries.forEach(function (gallery) {
      var index = gallery.dataset.obbGallery;
      var swiperEl = gallery.querySelector('[data-obb-swiper]');
      if (!swiperEl) return;

      var thumbs = gallery.querySelectorAll('[data-obb-thumb]');
      var slideCount = swiperEl.querySelectorAll('.swiper-slide').length;

      window.OrkaCarousel.create(
        swiperEl,
        {
          slidesPerView: 1,
          loop: slideCount > 1,
          navigation: {
            prevEl: gallery.querySelector('[data-obb-prev]'),
            nextEl: gallery.querySelector('[data-obb-next]')
          }
        },
        {
          onReady: function (swiper) {
            self.galleries[index] = swiper;

            function paint() {
              thumbs.forEach(function (thumb, i) {
                thumb.classList.toggle('is-active', i === swiper.realIndex);
              });
            }

            swiper.on('slideChange', paint);

            thumbs.forEach(function (thumb) {
              thumb.addEventListener('click', function () {
                var i = parseInt(thumb.dataset.index, 10);
                if (isNaN(i)) return;
                if (slideCount > 1) {
                  swiper.slideToLoop(i);
                } else {
                  swiper.slideTo(i);
                }
              });
            });

            paint();
          }
        }
      );
    });
  };

  /* The variant the current flavour + pack buys, and how many of it. A pack
     with its own variant is bought once; otherwise the flavour's base variant
     is added `multiplier` times. Both shapes carry variantId, available,
     price, compareAt and plans. */
  BuyBox.prototype.currentVariant = function (flavor, pack) {
    var own = flavor.packVariants && flavor.packVariants[this.packIndex];
    if (own) return { variant: own, quantity: 1 };
    return { variant: flavor, quantity: pack.multiplier || 1 };
  };

  BuyBox.prototype.currentPlan = function (variant) {
    if (!variant.plans || !variant.plans.length) return null;
    if (this.planId) {
      for (var i = 0; i < variant.plans.length; i++) {
        if (variant.plans[i].id === this.planId) return variant.plans[i];
      }
    }
    return variant.plans[0];
  };

  BuyBox.prototype.money = function (cents) {
    return formatMoney(cents, this.data.moneyFormat);
  };

  BuyBox.prototype.update = function () {
    var flavor = this.data.flavors[this.flavorIndex];
    var pack = this.data.packs[this.packIndex] || { multiplier: 1, units: 1, discount: 0 };
    if (!flavor) return;

    this.syncYotpo(flavor);

    var buy = this.currentVariant(flavor, pack);
    var variant = buy.variant;
    var plan = this.currentPlan(variant);

    // A flavour with no selling plan can only be bought one-time. Hide the
    // subscribe card entirely rather than leaving an option that won't select.
    var hasPlans = !!plan;
    if (!hasPlans) this.purchase = 'onetime';

    var mult = buy.quantity;

    // The cart receives quantity = mult (1 for a real pack variant), so the
    // honest base price is a straight multiple of the variant price. `pack.discount` is 0 unless the merchant
    // has declared a volume discount they actually created in Shopify — the
    // theme never invents one. verifyAgainstCart() below checks any declared
    // figure against the real cart and warns on drift.
    var volumeOff = pack.discount || 0;

    var listTotal = variant.price * mult;
    var oneTimeTotal = Math.max(0, listTotal - volumeOff);
    var subTotal = plan ? Math.max(0, plan.price * mult - volumeOff) : oneTimeTotal;

    var compareUnit = variant.compareAt > variant.price ? variant.compareAt : variant.price;
    // Strike through whichever is higher: the RRP, or the undiscounted multiple.
    var compareTotal = Math.max(compareUnit * mult, volumeOff > 0 ? listTotal : 0);

    this.expectedDiscount = volumeOff;
    this.expectedVariantId = variant.variantId;

    var activeTotal = this.purchase === 'subscribe' && plan ? subTotal : oneTimeTotal;

    /* ---- flavour colour drives every accent in the section ---- */
    this.root.style.setProperty('--orka-flavor', flavor.color);
    // Packs get their own tint so a white flavour (Variety Pack) still shows
    // which quantity is selected. Falls back to the flavour colour.
    this.root.style.setProperty('--orka-pack-color', flavor.packColor || flavor.color);

    /* ---- gallery ---- */
    var self = this;
    this.el.galleries.forEach(function (gallery) {
      var isActive = parseInt(gallery.dataset.obbGallery, 10) === self.flavorIndex;
      gallery.hidden = !isActive;
      if (isActive) {
        var swiper = self.galleries[gallery.dataset.obbGallery];
        // Hidden elements measure as zero width, so re-measure on reveal.
        if (swiper) requestAnimationFrame(function () { swiper.update(); });
      }
    });

    /* ---- title ---- */
    if (this.el.title) this.el.title.textContent = flavor.title;

    /* ---- selection state ---- */
    this.el.flavors.forEach(function (btn) {
      btn.classList.toggle('is-active', parseInt(btn.dataset.obbFlavor, 10) === self.flavorIndex);
      btn.setAttribute('aria-pressed', parseInt(btn.dataset.obbFlavor, 10) === self.flavorIndex ? 'true' : 'false');
    });

    this.el.packs.forEach(function (btn) {
      btn.classList.toggle('is-active', parseInt(btn.dataset.obbPack, 10) === self.packIndex);
      btn.setAttribute('aria-pressed', parseInt(btn.dataset.obbPack, 10) === self.packIndex ? 'true' : 'false');
    });

    this.el.options.forEach(function (opt) {
      var isSubscribe = opt.dataset.obbOption === 'subscribe';
      if (isSubscribe) opt.hidden = !hasPlans;

      var on = opt.dataset.obbOption === self.purchase;
      opt.classList.toggle('is-active', on);
      opt.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    /* ---- prices ---- */
    if (this.el.priceCurrent) this.el.priceCurrent.textContent = this.money(activeTotal);
    if (this.el.priceCompare) {
      this.el.priceCompare.textContent = compareTotal > activeTotal ? this.money(compareTotal) : '';
    }

    var units = pack.units || 1;

    if (this.el.subPrice && plan) this.el.subPrice.textContent = this.money(subTotal);
    if (this.el.subUnit && plan) {
      this.el.subUnit.textContent = this.money(subTotal / units) + '/' + this.data.unitLabel;
    }
    if (this.el.subBadge) {
      // The real subscription saving: one-time price minus subscription price.
      // Measuring against compare-at instead would fold the RRP strike-through
      // into the badge and overstate it.
      var saving = oneTimeTotal - subTotal;
      this.el.subBadge.textContent = saving > 0 ? this.money(saving) + ' off' : '';
      this.el.subBadge.hidden = saving <= 0;
    }

    if (this.el.otPrice) this.el.otPrice.textContent = this.money(oneTimeTotal);
    if (this.el.otUnit) {
      this.el.otUnit.textContent = this.money(oneTimeTotal / units) + '/' + this.data.unitLabel;
    }

    /* ---- delivery frequency options follow the variant ---- */
    if (this.el.planSelect && plan) {
      var wanted = variant.plans.map(function (p) { return p.id; }).join(',');
      if (this.el.planSelect.dataset.planIds !== wanted) {
        this.el.planSelect.dataset.planIds = wanted;
        this.el.planSelect.innerHTML = variant.plans
          .map(function (p) {
            return '<option value="' + p.id + '">' + p.name + '</option>';
          })
          .join('');
      }
      this.el.planSelect.value = String(plan.id);
    }

    /* ---- form inputs ---- */
    if (this.el.variantInput) this.el.variantInput.value = variant.variantId;
    if (this.el.qtyInput) this.el.qtyInput.value = mult;
    if (this.el.planInput) {
      this.el.planInput.value = this.purchase === 'subscribe' && plan ? plan.id : '';
    }

    /* ---- add to cart ---- */
    if (this.el.atc) {
      var soldOut = !variant.available;
      this.el.atc.disabled = soldOut;
      this.el.atc.classList.toggle('is-disabled', soldOut);
      if (this.el.atcText) {
        this.el.atcText.textContent = soldOut ? this.data.soldOutText : this.data.addText;
      }
    }
  };

  /* A declared volume discount is only a claim until the cart agrees. After an
     add, read the real cart line and compare. Console-only: this is a build-time
     safety net for the merchant, never customer-facing. */
  BuyBox.prototype.verifyAgainstCart = function () {
    var self = this;
    if (!this.expectedDiscount) return;

    var flavor = this.data.flavors[this.flavorIndex];
    var pack = this.data.packs[this.packIndex];
    if (!flavor || !pack) return;

    fetch((window.theme && theme.routes && theme.routes.cart) || '/cart.js', {
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].variant_id === self.expectedVariantId) { line = cart.items[i]; break; }
        }
        if (!line) return;

        var actual = 0;
        (line.line_level_discount_allocations || []).forEach(function (d) {
          actual += d.amount;
        });

        var expected = self.expectedDiscount;
        if (Math.abs(actual - expected) > 1) {
          console.warn(
            '[Orka] Pack "' + pack.label + '" declares ' + self.money(expected) +
            ' off, but the cart applied ' + self.money(actual) + '.' +
            (actual === 0
              ? ' No matching Shopify discount was found — clear the pack\'s "Volume discount" field or create the discount.'
              : ' Update the pack setting so the page matches checkout.')
          );
        }
      })
      .catch(function () {});
  };

  /* Flavours are separate products, so the Yotpo star-rating / reviews app
     blocks — server-rendered for whichever product the page loaded with —
     go stale the moment the buy box switches flavour client-side. Yotpo
     exposes no "swap product" event, so this pokes its widget container
     directly: repoint each mounted widget's product id and ask Yotpo to
     re-init that instance in place. No-ops once already synced, and safely
     no-ops if Yotpo's script hasn't attached window.yotpoWidgetsContainer
     yet (it'll catch up on the next flavour change). */
  BuyBox.prototype.syncYotpo = function (flavor) {
    if (!flavor || !flavor.id || this.yotpoProductId === flavor.id) return;

    var container = window.yotpoWidgetsContainer;
    if (!container || typeof container.initWidget !== 'function') return;

    var widgets = document.querySelectorAll('.yotpo-widget-instance');
    if (!widgets.length) return;

    for (var i = 0; i < widgets.length; i++) {
      var el = widgets[i];
      var instanceId = el.getAttribute('data-yotpo-instance-id');
      if (!instanceId) continue;
      el.setAttribute('data-yotpo-product-id', flavor.id);
      container.initWidget(instanceId, el);
    }

    this.yotpoProductId = flavor.id;
  };

  /* Keep the address bar on the flavour (and pack variant, when the pack is a
     real variant) being viewed without a reload, so the link reopens it. */
  BuyBox.prototype.syncUrl = function () {
    var flavor = this.data.flavors[this.flavorIndex];
    if (!flavor || !flavor.url || !window.history || !window.history.replaceState) return;
    var own = flavor.packVariants && flavor.packVariants[this.packIndex];
    // Other query params (utm_*, etc.) are kept; only ?variant= is ours.
    var url = new URL(window.location.href);
    url.pathname = flavor.url;
    if (own) {
      url.searchParams.set('variant', own.variantId);
    } else {
      url.searchParams.delete('variant');
    }
    if (url.href === window.location.href) return;
    window.history.replaceState({ orkaFlavor: flavor.handle }, '', url.href);
  };

  BuyBox.prototype.renderEta = function () {
    if (!this.el.eta || !this.data.eta) return;
    var range = deliveryRange(this.data.eta);
    var template = this.data.eta.template || '';
    this.el.eta.innerHTML = template.replace('[date]', '<strong>' + range + '</strong>');
  };

  /* ---------------------------------------------------------------- boot */

  function initAll(root) {
    (root || document).querySelectorAll('[data-orka-buy-box]').forEach(function (el) {
      new BuyBox(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
